import { base44 } from '@/api/base44Client';

// 1. Konversi Bulan ke Huruf Alfabet (1=A, 2=B, ..., 8=H, ..., 12=L)
export const MONTH_LETTERS = {
  1: 'A', 2: 'B', 3: 'C', 4: 'D', 5: 'E', 6: 'F',
  7: 'G', 8: 'H', 9: 'I', 10: 'J', 11: 'K', 12: 'L'
};

export const getMonthLetter = (monthNumberOrDate) => {
  let m = 1;
  if (monthNumberOrDate instanceof Date) {
    m = monthNumberOrDate.getMonth() + 1;
  } else if (typeof monthNumberOrDate === 'string' && monthNumberOrDate.includes('-')) {
    const parts = monthNumberOrDate.split('-');
    m = parseInt(parts[1], 10) || 1;
  } else {
    m = parseInt(monthNumberOrDate, 10) || 1;
  }
  return MONTH_LETTERS[m] || 'A';
};

// 2. Format Singkatan Kode Brand (Maksimal 6 Huruf Kapital, Alphanumeric)
export const formatBrandCode = (brandName, customCode) => {
  if (customCode && customCode.trim()) {
    return customCode.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
  }
  if (!brandName) return 'SATALA';
  // Ambil hanya nama brand murni (buang nama PIC setelah '/' atau keterangan kurung)
  const pureBrand = String(brandName).split('/')[0].split('(')[0].trim();
  return pureBrand.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
};

// Cek apakah kode brand sudah dipakai oleh brand lain (mencegah duplikat)
export const checkBrandCodeCollision = (candidateCode, existingMasters = [], currentBrandId = null) => {
  if (!candidateCode) return { isConflict: false, conflictBrand: null };
  const cleanCode = candidateCode.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().trim();
  if (!cleanCode) return { isConflict: false, conflictBrand: null };

  const collision = (existingMasters || []).find(b => {
    if (currentBrandId && b.id === currentBrandId) return false;
    return (b.brand_code || '').toUpperCase().trim() === cleanCode;
  });

  return collision ? { isConflict: true, conflictBrand: collision } : { isConflict: false, conflictBrand: null };
};

// Generator otomatis kode brand yang 100% unik dan tidak akan pernah sama/tabrakan
export const generateUniqueBrandCode = (brandName = '', customCode = '', existingMasters = [], currentBrandId = null) => {
  const pureBrand = String(brandName || '').split('/')[0].split('(')[0].trim();
  const existingCodes = new Set(
    (existingMasters || [])
      .filter(b => !currentBrandId || b.id !== currentBrandId)
      .map(b => (b.brand_code || '').toUpperCase().trim())
      .filter(Boolean)
  );

  let base = (customCode || pureBrand || 'BRAND')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .trim();
  
  if (!base) base = 'BRAND';
  base = base.slice(0, 6);

  // Jika kode dasar belum dipakai brand manapun, gunakan langsung
  if (!existingCodes.has(base)) {
    return base;
  }

  // Jika terjadi tabrakan (misal: OXGN sudah dipakai brand lain):
  // 1. Cari variasi unik dari nama brand
  const cleanName = pureBrand.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().trim();
  const candidates = [];

  // Variasi panjang potongan (misal: OXYG, OXYGA, OXYGAI)
  for (let len = 4; len <= Math.min(6, cleanName.length); len++) {
    const cand = cleanName.slice(0, len);
    if (cand && !candidates.includes(cand)) candidates.push(cand);
  }

  // Variasi konsonan
  const consonants = cleanName.replace(/[AEIOU]/g, '');
  if (consonants.length >= 3) {
    for (let len = 3; len <= Math.min(6, consonants.length); len++) {
      const cand = consonants.slice(0, len);
      if (cand && !candidates.includes(cand)) candidates.push(cand);
    }
  }

  // Variasi kata (jika lebih dari 1 kata, misal "Oxy Gain" -> OXGA, OG)
  const words = pureBrand.split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    const acronym = words.map(w => w[0]).join('').toUpperCase().slice(0, 6);
    if (acronym && !candidates.includes(acronym)) candidates.push(acronym);
    
    const blended = (words[0].slice(0, 3) + words[1].slice(0, 3)).toUpperCase().slice(0, 6);
    if (blended && !candidates.includes(blended)) candidates.push(blended);
  }

  for (const cand of candidates) {
    if (cand.length >= 3 && !existingCodes.has(cand)) {
      return cand;
    }
  }

  // 2. Fallback: Tambahkan angka unik di belakang (misal: OXGN2, OXGN3, dst)
  const root = base.slice(0, 5);
  for (let i = 2; i <= 9; i++) {
    const cand = `${root}${i}`;
    if (!existingCodes.has(cand)) return cand;
  }

  const root4 = base.slice(0, 4);
  for (let i = 1; i <= 99; i++) {
    const cand = `${root4}${String(i).padStart(2, '0')}`;
    if (!existingCodes.has(cand)) return cand;
  }

  return base;
};

// 3. Formula Pembuatan Nomor Batch (Contoh: ARTHEA012626H01)
export const generateBatchCode = ({
  brandCode = 'SATALA',
  variantIndex = 1,
  dateStr = new Date().toISOString().slice(0, 10),
  monthlyCount = 1
}) => {
  const cleanBrand = formatBrandCode('', brandCode);
  const cleanVarIdx = String(variantIndex).padStart(2, '0');
  
  // Parse Tanggal (DD) dan Tahun (YY)
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const year2Digit = String(d.getFullYear()).slice(-2);
  const monthLetter = getMonthLetter(d);
  const cleanCount = String(monthlyCount).padStart(2, '0');

  // Format: [BRAND 6][VARIAN 2][TANGGAL 2][TAHUN 2][BULAN_LETTER 1][COUNT 2]
  return `${cleanBrand}${cleanVarIdx}${day}${year2Digit}${monthLetter}${cleanCount}`;
};

// Master Data Lengkap dari Spreadsheet (110 Brand Terdaftar)
export const INITIAL_BATCH_MASTER = [
  {
    "id": "batch_master_7freund_0",
    "brand_name": "7FREUND",
    "brand_code": "7FREUN",
    "variants": [
      {
        "code": "01",
        "name": "PISTACHIO HUGS",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "PINEAPPLE KISS",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_ibperfume_1",
    "brand_name": "IBPERFUME",
    "brand_code": "IBPERF",
    "variants": [
      {
        "code": "01",
        "name": "Anggun",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Eksekutif",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Elegant",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Premium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Glamour",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_intersecsion_2",
    "brand_name": "INTERSECSION",
    "brand_code": "INTERS",
    "variants": [
      {
        "code": "01",
        "name": "#01",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "#02",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "#03",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "#04",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "#05",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_jazira_3",
    "brand_name": "JAZIRA",
    "brand_code": "JAZIRA",
    "variants": [
      {
        "code": "01",
        "name": "Malika",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Arsalan",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_jihan_parfume_4",
    "brand_name": "JIHAN PARFUME",
    "brand_code": "JIHANP",
    "variants": [
      {
        "code": "01",
        "name": "Bacarat",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "V. Romantic",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "T. Swift",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Avril",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "V. Scandal",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Selena",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_julis_5",
    "brand_name": "JULIS",
    "brand_code": "JULIS",
    "variants": [
      {
        "code": "01",
        "name": "Scandal",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Coffee Cream",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Strawberry Poundcake",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Popcorn Vanilla",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Bubblegum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Ice Cream Chocolate",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Freshtea",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Vanilla Cake",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Mango Cream Dessert",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Milkshake Banana",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Pink Beach",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Luckymen",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "Sweet Direction",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_kanza_arum_6",
    "brand_name": "KANZA ARUM",
    "brand_code": "KANZAA",
    "variants": [
      {
        "code": "01",
        "name": "Mens'sweat",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Mukhalat Yaman",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Minyak Gaharu",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_kokulu_7",
    "brand_name": "KOKULU",
    "brand_code": "KOKULU",
    "variants": [
      {
        "code": "01",
        "name": "In Shine Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Dr. Blooming Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "P. Baby Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Evril Frbdn Rose Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Clinic Happy Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "P. Hilton Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Melon Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Million Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Kirkee Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Romanwish Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Vanilla Bloom",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Lucky Women",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "B. Gum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "14",
        "name": "B. Coffee",
        "registered_date": "2026-01-01"
      },
      {
        "code": "15",
        "name": "B. Acua",
        "registered_date": "2026-01-01"
      },
      {
        "code": "16",
        "name": "Omni Amethyste",
        "registered_date": "2026-01-01"
      },
      {
        "code": "17",
        "name": "Acua Kiss Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "18",
        "name": "Eros",
        "registered_date": "2026-01-01"
      },
      {
        "code": "19",
        "name": "Good Girl",
        "registered_date": "2026-01-01"
      },
      {
        "code": "20",
        "name": "Eros FLM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "21",
        "name": "VP Men Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "22",
        "name": "Bcrt Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "23",
        "name": "Red Vanilla Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "24",
        "name": "DNL Blue Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "25",
        "name": "Melati Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "26",
        "name": "Slavina Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "27",
        "name": "Million Lucky Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "28",
        "name": "Dr. Savage Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "29",
        "name": "VSt Scandal Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "30",
        "name": "Black OPM Eau De Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "31",
        "name": "Zulton",
        "registered_date": "2026-01-01"
      },
      {
        "code": "32",
        "name": "Pink Chify",
        "registered_date": "2026-01-01"
      },
      {
        "code": "33",
        "name": "GC Bloom",
        "registered_date": "2026-01-01"
      },
      {
        "code": "34",
        "name": "Anger Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "35",
        "name": "Taylor Swift",
        "registered_date": "2026-01-01"
      },
      {
        "code": "36",
        "name": "VST Bmshel",
        "registered_date": "2026-01-01"
      },
      {
        "code": "37",
        "name": "Cokelat",
        "registered_date": "2026-01-01"
      },
      {
        "code": "38",
        "name": "Vanilla Ice Cream",
        "registered_date": "2026-01-01"
      },
      {
        "code": "39",
        "name": "Cuddle",
        "registered_date": "2026-01-01"
      },
      {
        "code": "40",
        "name": "J Still",
        "registered_date": "2026-01-01"
      },
      {
        "code": "41",
        "name": "One Dir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "42",
        "name": "Citrus Serenade",
        "registered_date": "2026-01-01"
      },
      {
        "code": "43",
        "name": "Solea",
        "registered_date": "2026-01-01"
      },
      {
        "code": "44",
        "name": "Vespera",
        "registered_date": "2026-01-01"
      },
      {
        "code": "45",
        "name": "Calmea",
        "registered_date": "2026-01-01"
      },
      {
        "code": "46",
        "name": "IMAGYA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "47",
        "name": "VELORA SEA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "48",
        "name": "LYBERIA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "49",
        "name": "BAY THE WAY",
        "registered_date": "2026-01-01"
      },
      {
        "code": "50",
        "name": "LEGACY",
        "registered_date": "2026-01-01"
      },
      {
        "code": "51",
        "name": "GENTLEMEN",
        "registered_date": "2026-01-01"
      },
      {
        "code": "52",
        "name": "GOLDEN VANILLE",
        "registered_date": "2026-01-01"
      },
      {
        "code": "53",
        "name": "NOLAND",
        "registered_date": "2026-01-01"
      },
      {
        "code": "54",
        "name": "JOEY",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_kylo_amour_8",
    "brand_name": "KYLO AMOUR",
    "brand_code": "KYLOAM",
    "variants": [
      {
        "code": "01",
        "name": "Citrus Serenade",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Million luck",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Savage",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Blck Opm",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Scandl",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_khumsava_9",
    "brand_name": "KHUMSAVA",
    "brand_code": "KHUMSA",
    "variants": [
      {
        "code": "01",
        "name": "Anila",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Dikara",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Harsa",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_lakuku_10",
    "brand_name": "LAKUKU",
    "brand_code": "LAKUKU",
    "variants": [
      {
        "code": "01",
        "name": "Lalova",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Man Woody",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Ambrose",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Jubilee",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Orient",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Nova Haven",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_lavior_s_11",
    "brand_name": "LAVIOR'S",
    "brand_code": "LAVIOR",
    "variants": [
      {
        "code": "01",
        "name": "EDEN",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "TERRA",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_lois_7_seven_12",
    "brand_name": "LOIS 7 SEVEN",
    "brand_code": "LOIS7S",
    "variants": [
      {
        "code": "01",
        "name": "Begitu Cantik",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Perhatianmu",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Special untukmu",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_lokaland_13",
    "brand_name": "LOKALAND",
    "brand_code": "LOKALA",
    "variants": [
      {
        "code": "01",
        "name": "Byolluna",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Syagar Eden",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Dolce Nivoce",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_lunair_14",
    "brand_name": "LUNAIR",
    "brand_code": "LUNAIR",
    "variants": [
      {
        "code": "01",
        "name": "Morning Shower",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Alpha Blossom",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Wanderlust",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_mahhabe_luxuria_15",
    "brand_name": "MAHHABE LUXURIA",
    "brand_code": "MAHHAB",
    "variants": [
      {
        "code": "01",
        "name": "Infinity",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Jennaty",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Nirwarna",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_manifest_16",
    "brand_name": "MANIFEST",
    "brand_code": "MANIFE",
    "variants": [
      {
        "code": "01",
        "name": "Lotsa De Casha",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Viva La Lova",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_meet_me_at_the_altar_17",
    "brand_name": "MEET ME AT THE ALTAR",
    "brand_code": "MEETME",
    "variants": [
      {
        "code": "01",
        "name": "Passiflora",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Paradiso",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Nigredo",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Salvatio",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Pamona",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Insomnus",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_meet_the_maker_18",
    "brand_name": "MEET THE MAKER",
    "brand_code": "MEETTH",
    "variants": [
      {
        "code": "01",
        "name": "Mies",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Shift",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Venturi",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_maison_garnet_19",
    "brand_name": "MAISON GARNET",
    "brand_code": "MAISON",
    "variants": [
      {
        "code": "01",
        "name": "Baby's Breath",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Alhambra Rose",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Mugunghwa Peach",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_mine_lux_20",
    "brand_name": "MINE LUX",
    "brand_code": "MINELU",
    "variants": [
      {
        "code": "01",
        "name": "De Marlex Delina",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Gcci Guilty Black",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Mad About you",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Jaguar Black",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Kopi Bali",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Matcha",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Do Son",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "O Nomade",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Angel Share",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Les Sables Rose",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "212 S Women",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Chnl De Bleu",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "Tobaco Vanilla",
        "registered_date": "2026-01-01"
      },
      {
        "code": "14",
        "name": "C. Aventus",
        "registered_date": "2026-01-01"
      },
      {
        "code": "15",
        "name": "A. Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "16",
        "name": "B. Aqua",
        "registered_date": "2026-01-01"
      },
      {
        "code": "17",
        "name": "V. Eros",
        "registered_date": "2026-01-01"
      },
      {
        "code": "18",
        "name": "N. Slavina",
        "registered_date": "2026-01-01"
      },
      {
        "code": "19",
        "name": "Bcrt 540",
        "registered_date": "2026-01-01"
      },
      {
        "code": "20",
        "name": "Bombshell",
        "registered_date": "2026-01-01"
      },
      {
        "code": "21",
        "name": "Black Musk",
        "registered_date": "2026-01-01"
      },
      {
        "code": "22",
        "name": "Polo Sport",
        "registered_date": "2026-01-01"
      },
      {
        "code": "23",
        "name": "Vanilla Cake",
        "registered_date": "2026-01-01"
      },
      {
        "code": "24",
        "name": "Scandalous",
        "registered_date": "2026-01-01"
      },
      {
        "code": "25",
        "name": "Chnl Coco",
        "registered_date": "2026-01-01"
      },
      {
        "code": "26",
        "name": "Extreme",
        "registered_date": "2026-01-01"
      },
      {
        "code": "27",
        "name": "P. Wanita",
        "registered_date": "2026-01-01"
      },
      {
        "code": "28",
        "name": "A. Grande",
        "registered_date": "2026-01-01"
      },
      {
        "code": "29",
        "name": "Dr. Savage",
        "registered_date": "2026-01-01"
      },
      {
        "code": "30",
        "name": "B. Opium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "31",
        "name": "Z. Man",
        "registered_date": "2026-01-01"
      },
      {
        "code": "32",
        "name": "Vanila",
        "registered_date": "2026-01-01"
      },
      {
        "code": "33",
        "name": "Black Xs L'Exces",
        "registered_date": "2026-01-01"
      },
      {
        "code": "34",
        "name": "212 VIP Man",
        "registered_date": "2026-01-01"
      },
      {
        "code": "35",
        "name": "Dnhl London",
        "registered_date": "2026-01-01"
      },
      {
        "code": "36",
        "name": "Jaguar Vision",
        "registered_date": "2026-01-01"
      },
      {
        "code": "37",
        "name": "Pesona Pria",
        "registered_date": "2026-01-01"
      },
      {
        "code": "38",
        "name": "M. Zain",
        "registered_date": "2026-01-01"
      },
      {
        "code": "39",
        "name": "H. Boss Energize",
        "registered_date": "2026-01-01"
      },
      {
        "code": "40",
        "name": "Miss D Blooming",
        "registered_date": "2026-01-01"
      },
      {
        "code": "41",
        "name": "Aigner Black",
        "registered_date": "2026-01-01"
      },
      {
        "code": "42",
        "name": "Dunhill Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "43",
        "name": "Million Lucky",
        "registered_date": "2026-01-01"
      },
      {
        "code": "44",
        "name": "Night Cream",
        "registered_date": "2026-01-01"
      },
      {
        "code": "45",
        "name": "Toner",
        "registered_date": "2026-01-01"
      },
      {
        "code": "46",
        "name": "Serum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "47",
        "name": "Day Cream",
        "registered_date": "2026-01-01"
      },
      {
        "code": "48",
        "name": "Facial Wash Brightening",
        "registered_date": "2026-01-01"
      },
      {
        "code": "49",
        "name": "Solaris",
        "registered_date": "2026-01-01"
      },
      {
        "code": "50",
        "name": "Ultra male",
        "registered_date": "2026-01-01"
      },
      {
        "code": "51",
        "name": "Pdm althar",
        "registered_date": "2026-01-01"
      },
      {
        "code": "52",
        "name": "Hawas ice",
        "registered_date": "2026-01-01"
      },
      {
        "code": "53",
        "name": "Khamra",
        "registered_date": "2026-01-01"
      },
      {
        "code": "54",
        "name": "Le male elixir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "55",
        "name": "9pm rebel",
        "registered_date": "2026-01-01"
      },
      {
        "code": "56",
        "name": "SOTB",
        "registered_date": "2026-01-01"
      },
      {
        "code": "57",
        "name": "Reef 33",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_mississippi_ladies_21",
    "brand_name": "MISSISSIPPI LADIES",
    "brand_code": "MISSIS",
    "variants": [
      {
        "code": "01",
        "name": "Solace",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Untamed",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Vows",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_mussera_22",
    "brand_name": "MUSSERA",
    "brand_code": "MUSSER",
    "variants": [
      {
        "code": "01",
        "name": "a Quiet Violet",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Where the ocean Sleeps",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Drop of Joy",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "The Bloom Essence",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_muv_23",
    "brand_name": "MUV",
    "brand_code": "MUV",
    "variants": [
      {
        "code": "01",
        "name": "Dr. Savage",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Slavina",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Lavender",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Ehsas Arabia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "P. Wanita",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Kasturi Kijang",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Eigner Blue X vanilla Ice",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Cewek Dingin",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Cowok Sigma",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Rotiboy",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "DHL Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Eigner Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "C.O.D Cowok Dingin",
        "registered_date": "2026-01-01"
      },
      {
        "code": "14",
        "name": "S.A.Y Sensual Aura Of Youth",
        "registered_date": "2026-01-01"
      },
      {
        "code": "15",
        "name": "P.O.V Player Of Venom",
        "registered_date": "2026-01-01"
      },
      {
        "code": "16",
        "name": "Slavina Super Premium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "17",
        "name": "DHL Blue Super Premium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "18",
        "name": "Dr Savage Super Premium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "19",
        "name": "Eigner Blue Super Premium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "20",
        "name": "P. Wanita Super Premium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "21",
        "name": "B. OPIUM X BACARAT SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "22",
        "name": "DHL BLUE X SCANDALUS SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "23",
        "name": "B. OPIUM X BACARAT SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "24",
        "name": "MILION LUCKY SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "25",
        "name": "EIGNER BLACK SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "26",
        "name": "VEROS FLAME SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "27",
        "name": "PINKCIFFON SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "28",
        "name": "EIGNER BLUE X MILION LUCKY SUPER PREMIUM",
        "registered_date": "2026-01-01"
      },
      {
        "code": "29",
        "name": "Rebellion",
        "registered_date": "2026-01-01"
      },
      {
        "code": "30",
        "name": "Queen Noir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "31",
        "name": "Empire Noir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "32",
        "name": "Midnight Boss",
        "registered_date": "2026-01-01"
      },
      {
        "code": "33",
        "name": "Bella Royale",
        "registered_date": "2026-01-01"
      },
      {
        "code": "34",
        "name": "Black Cartel",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_mf_marfyn_24",
    "brand_name": "MF MARFYN",
    "brand_code": "MFMARF",
    "variants": [
      {
        "code": "01",
        "name": "Musco",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Fresco",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_nafs_25",
    "brand_name": "NAFS",
    "brand_code": "NAFS",
    "variants": [
      {
        "code": "01",
        "name": "Rumiver",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Rumilion",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "N.O.R Nafs of Rumi",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_nmdn_26",
    "brand_name": "NMDN",
    "brand_code": "NMDN",
    "variants": [
      {
        "code": "01",
        "name": "Lush Of Sicily",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Rosa Anatolia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Trace Of Florence",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_nuver_27",
    "brand_name": "NUVER",
    "brand_code": "NUVER",
    "variants": [
      {
        "code": "01",
        "name": "Inti More",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_north_prime_28",
    "brand_name": "NORTH PRIME",
    "brand_code": "NORTHP",
    "variants": [
      {
        "code": "01",
        "name": "Shadow",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Noir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Rado",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Leather",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_natur_you_29",
    "brand_name": "NATUR YOU",
    "brand_code": "NATURY",
    "variants": [
      {
        "code": "01",
        "name": "Herba Mint",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Soft Vanilla",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Fresh Guava",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Bloom Lychee",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Morning coffee",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_noor_parfume_30",
    "brand_name": "NOOR PARFUME",
    "brand_code": "NOORPA",
    "variants": [
      {
        "code": "01",
        "name": "Nau Lunar",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Coffee Time",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "CIRCLÉ",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "MADEM ROSÉ",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_pazzione_31",
    "brand_name": "PAZZIONE",
    "brand_code": "PAZZIO",
    "variants": [
      {
        "code": "01",
        "name": "Lemilion",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_predire_32",
    "brand_name": "PREDIRE",
    "brand_code": "PREDIR",
    "variants": [
      {
        "code": "01",
        "name": "Delish Library",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "ORANGE POIVREE",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "FORBIDDEN IRIS",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_pop_of_mingle_33",
    "brand_name": "POP OF MINGLE",
    "brand_code": "POPOFM",
    "variants": [
      {
        "code": "01",
        "name": "TOD",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Gossip",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Cuddle",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_purvu_34",
    "brand_name": "PURVU",
    "brand_code": "PURVU",
    "variants": [
      {
        "code": "01",
        "name": "Ariana",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Aisha",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_platonic_35",
    "brand_name": "PLATONIC",
    "brand_code": "PLATON",
    "variants": [
      {
        "code": "01",
        "name": "LINGER",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "DAY ONE",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "MUTUAL SPACE",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_omia_36",
    "brand_name": "OMIA",
    "brand_code": "OMIA",
    "variants": [
      {
        "code": "01",
        "name": "Auvier",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Pretiosa",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Valorous",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "VARSHE",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "SHAVARA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "VARUNA",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_oude_37",
    "brand_name": "OUDE",
    "brand_code": "OUDE",
    "variants": [
      {
        "code": "01",
        "name": "Floral Allure",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Suede No. 8",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Gentle Waters",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Suede Eight",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Flora Allure",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_oudixa_38",
    "brand_name": "OUDIXA",
    "brand_code": "OUDIXA",
    "variants": [
      {
        "code": "01",
        "name": "Al Layali",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Al Fakher",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Al Khamra",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_oxgn_39",
    "brand_name": "OXGN",
    "brand_code": "OXGN",
    "variants": [
      {
        "code": "01",
        "name": "Rosmelt",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Cinnamon Luxe",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Golden Kiss",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Starla",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Pramugari",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Chiffony",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_oxta_40",
    "brand_name": "OXTA",
    "brand_code": "OXTA",
    "variants": [
      {
        "code": "01",
        "name": "Odhin",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Achilles For Women",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Zeus For Unisex",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Olentia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Athena",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Aphrodite",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Poseidon",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Minos",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Selena",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Acantha",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Moskov",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Thor",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_oxygain_41",
    "brand_name": "OXYGAIN",
    "brand_code": "OXYGAI",
    "variants": [
      {
        "code": "01",
        "name": "Bravery Adventus",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Belaluna B.Opium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Male U.Male",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Mighty Veros Men",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Panamera Tbco Vanilla",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Victor Dh.Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Rapido Vip Man",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Pablo Million Lucky",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Hermosa Scandalous",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Shelby Agnr Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Carisma Blue De Ch",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Calma Vanila",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "Bel Homme Savage",
        "registered_date": "2026-01-01"
      },
      {
        "code": "14",
        "name": "Voiz Strong With You",
        "registered_date": "2026-01-01"
      },
      {
        "code": "15",
        "name": "Y for Men",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_ozkan_42",
    "brand_name": "OZKAN",
    "brand_code": "OZKAN",
    "variants": [
      {
        "code": "01",
        "name": "Most Wamted",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Klmax Durasi",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Rich &amp; Famous",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "For Him",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_olfera_43",
    "brand_name": "OLFERA",
    "brand_code": "OLFERA",
    "variants": [
      {
        "code": "01",
        "name": "Sunset In satorini",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Midnight In Paris",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_orkai_44",
    "brand_name": "ORKAI",
    "brand_code": "ORKAI",
    "variants": [
      {
        "code": "01",
        "name": "EMOTION GAME",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "RAFAELA SECRET",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "MATCHA SERIES",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_radius_45",
    "brand_name": "RADIUS",
    "brand_code": "RADIUS",
    "variants": [
      {
        "code": "01",
        "name": "Vibe",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Prime",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Zenith",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Bloom",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Eclips",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_rexzel_46",
    "brand_name": "REXZEL",
    "brand_code": "REXZEL",
    "variants": [
      {
        "code": "01",
        "name": "Free for Watermelon",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Everyone is an Actor",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Stand Our Land",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Rainbow not Allowed",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_rkynd_47",
    "brand_name": "RKYND",
    "brand_code": "RKYND",
    "variants": [
      {
        "code": "01",
        "name": "Rebellion",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_s_amp_v_saint_verant_48",
    "brand_name": "S&amp;V SAINT VERANT",
    "brand_code": "SAMPVS",
    "variants": [
      {
        "code": "01",
        "name": "Bivol",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Sensual",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Vionic",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Savier",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Lucy Fresh",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Saviera",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Secret Man",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Alexis Oriental",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Sporty Man",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Lady Scandia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Oud Athir",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_salna_49",
    "brand_name": "SALNA",
    "brand_code": "SALNA",
    "variants": [
      {
        "code": "01",
        "name": "Sweet Grace",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Eveymoment",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Midnight Lust",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_salu_50",
    "brand_name": "SALU",
    "brand_code": "SALU",
    "variants": [
      {
        "code": "01",
        "name": "Toner",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Night cream Brightening",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Day cream Brightening",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Serum Brightening",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Facial Wash Brightening",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_saphrox_51",
    "brand_name": "SAPHROX",
    "brand_code": "SAPHRO",
    "variants": [
      {
        "code": "01",
        "name": "Blush",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Dusk",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_satta_52",
    "brand_name": "SATTA",
    "brand_code": "SATTA",
    "variants": [
      {
        "code": "01",
        "name": "Satta Deodorant Spray",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_savana_53",
    "brand_name": "SAVANA",
    "brand_code": "SAVANA",
    "variants": [
      {
        "code": "01",
        "name": "Scents Silk",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Scents Aurora",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Scents Prime",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Scents Satin",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Scents Aurum",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_secret_finest_54",
    "brand_name": "SECRET FINEST",
    "brand_code": "SECRET",
    "variants": [
      {
        "code": "01",
        "name": "Ambre",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Madera",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Neute",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Sade",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_sevnov_lab_55",
    "brand_name": "SEVNOV LAB",
    "brand_code": "SEVNOV",
    "variants": [
      {
        "code": "01",
        "name": "VILLA AVEN",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "VILLA SERA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "VILLA ORYN",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_segarra_parfum_56",
    "brand_name": "SEGARRA PARFUM",
    "brand_code": "SEGARR",
    "variants": [
      {
        "code": "01",
        "name": "Romantic Wish",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "B. Opium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Red Vanilla",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Eau Des Sents",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Blanchett",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Savage",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Cool Water",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Eroz",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Lavies",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Amethys",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Hypnotics Poison",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Peony",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "Wood Sage",
        "registered_date": "2026-01-01"
      },
      {
        "code": "14",
        "name": "Scandalouz",
        "registered_date": "2026-01-01"
      },
      {
        "code": "15",
        "name": "Myrrh And Tonka",
        "registered_date": "2026-01-01"
      },
      {
        "code": "16",
        "name": "Md Blooming",
        "registered_date": "2026-01-01"
      },
      {
        "code": "17",
        "name": "Freesia And English Pear",
        "registered_date": "2026-01-01"
      },
      {
        "code": "18",
        "name": "Dunblue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "19",
        "name": "Libre",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_sides_57",
    "brand_name": "SIDES",
    "brand_code": "SIDES",
    "variants": [
      {
        "code": "01",
        "name": "Lycette",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Lowkey",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "UNWIND",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_signa_58",
    "brand_name": "SIGNA",
    "brand_code": "SIGNA",
    "variants": [
      {
        "code": "01",
        "name": "Pallas",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Vesta",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Eunomia",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_simbiot_59",
    "brand_name": "SIMBIOT",
    "brand_code": "SIMBIO",
    "variants": [
      {
        "code": "01",
        "name": "Agony",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Venom",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Amora",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Doses",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Alien",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_somnium_60",
    "brand_name": "SOMNIUM",
    "brand_code": "SOMNIU",
    "variants": [
      {
        "code": "01",
        "name": "Whispers of Green",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Bleu de Nuit",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Cosmic Queen",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Oakfire",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Untitled",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Bitja No. 18",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_superdope__61",
    "brand_name": "SUPERDOPE!",
    "brand_code": "SUPERD",
    "variants": [
      {
        "code": "01",
        "name": "Iron Core",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Iron Night",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_swist_62",
    "brand_name": "SWIST",
    "brand_code": "SWIST",
    "variants": [
      {
        "code": "01",
        "name": "Cloud Whisaper",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Brulee",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_savero_63",
    "brand_name": "SAVERO",
    "brand_code": "SAVERO",
    "variants": [
      {
        "code": "01",
        "name": "LUNA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "ESSENCE",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "SOLACE",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "AFTERGLOW",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_aracasa_64",
    "brand_name": "ARACASA",
    "brand_code": "ARACAS",
    "variants": [
      {
        "code": "01",
        "name": "FLOOR 07 NUMBER 09",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_airthea_65",
    "brand_name": "AIRTHEA",
    "brand_code": "ARTHEA",
    "variants": [
      {
        "code": "01",
        "name": "Cakra",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Lunara",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Sirene",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_alamea_scents_66",
    "brand_name": "ALAMEA SCENTS",
    "brand_code": "ALAMEA",
    "variants": [
      {
        "code": "01",
        "name": "Midnight Roast",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Powder Reverie",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "The Rose Archive",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_albusterry_black_67",
    "brand_name": "ALBUSTERRY BLACK",
    "brand_code": "ALBUST",
    "variants": [
      {
        "code": "01",
        "name": "Diamond Lady",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Enchanting Pearl",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Prestige Wood",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Dapper Suave",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_alelas_68",
    "brand_name": "ALELAS",
    "brand_code": "ALELAS",
    "variants": [
      {
        "code": "01",
        "name": "De Ellina By Angelina",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Khasmire by Angelina",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Royal Blue",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_alkaz_69",
    "brand_name": "ALKAZ",
    "brand_code": "ALKAZ",
    "variants": [
      {
        "code": "01",
        "name": "Mosman",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Duomo",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Manly",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Azura",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_alkeya_70",
    "brand_name": "ALKEYA",
    "brand_code": "ALKEYA",
    "variants": [
      {
        "code": "01",
        "name": "Fresh Oasis Yahya",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Luxurious Kenara",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Luxurious Yasmin",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Fresh Oasis Ibrahim",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Luxurious Harika",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Luxurious Alika",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Herbal body wash Glowpamara",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Night Cream White Booster",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Day Cream Glowing",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_andor_studio_71",
    "brand_name": "ANDOR STUDIO",
    "brand_code": "ANDOR",
    "variants": [
      {
        "code": "01",
        "name": "Dance between the line",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Whisper of Citrus and Sea",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Either One",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_aow_72",
    "brand_name": "AOW",
    "brand_code": "AOW",
    "variants": [
      {
        "code": "01",
        "name": "Mighty Black Boldness",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Mighty Green Bravery",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Mighty Blue Courage",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Black Scent Gold Musk",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Black Scent Pink Blossom",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Black Scent Mint Macha",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Yolo Imagines",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Yolo Stones",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Yolo Rapsodi",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_aure_luxurius_73",
    "brand_name": "AURE LUXURIUS",
    "brand_code": "AURELU",
    "variants": [
      {
        "code": "01",
        "name": "Extrovert",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Introvert",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Posesif Black",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Lu Men",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Posesif Rose",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Aurora",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_tomo_laboratory_74",
    "brand_name": "TOMO LABORATORY",
    "brand_code": "TOMOLA",
    "variants": [
      {
        "code": "01",
        "name": "Hoya Carnosa",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Wild Erysimum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Lilac Carnival",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_tre_idn_perfume_75",
    "brand_name": "TRE IDN PERFUME",
    "brand_code": "TREIDN",
    "variants": [
      {
        "code": "01",
        "name": "Noore",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Hella",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Jorne",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Alora",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Luna",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Freyja",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Au De Pistache",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Echo Of Rain",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_ureru_76",
    "brand_name": "URERU",
    "brand_code": "URERU",
    "variants": [
      {
        "code": "01",
        "name": "Bloosome",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "mactha",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Escape",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Stone &amp; Shadow",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Cityscape",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_valord_create_gentleman_77",
    "brand_name": "VALORD CREATE GENTLEMAN",
    "brand_code": "VALORD",
    "variants": [
      {
        "code": "01",
        "name": "Nocturne Lite",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Kingsman Lite",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_velora_s_let_the_scent_do_the_talking_78",
    "brand_name": "VELORA'S LET THE SCENT DO THE TALKING",
    "brand_code": "VELORA",
    "variants": [
      {
        "code": "01",
        "name": "Drake's Inferno",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Aether Drift",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Violet Serenade",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Zarae Luxe",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Emberstone",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Althe Essence",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Ironwood Fury",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Opaline Reverie",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Celestria Rose",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Obsidian Night",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_veu_79",
    "brand_name": "VEU",
    "brand_code": "VEU",
    "variants": [
      {
        "code": "01",
        "name": "Umbra",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Umbara",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Askara",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_za_ny_80",
    "brand_name": "ZA.NY",
    "brand_code": "ZANY",
    "variants": [
      {
        "code": "01",
        "name": "Solaris",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Down To Earth",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Coco",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Tmfrd Toba Vanilla",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Ultramale",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Omnia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Enchanted",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Pink Laundry",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Ombre Leather oud",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Signature of java",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Chno",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Las Pozas",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "Loui",
        "registered_date": "2026-01-01"
      },
      {
        "code": "14",
        "name": "Ostara",
        "registered_date": "2026-01-01"
      },
      {
        "code": "15",
        "name": "Sotb",
        "registered_date": "2026-01-01"
      },
      {
        "code": "16",
        "name": "Unrosed",
        "registered_date": "2026-01-01"
      },
      {
        "code": "17",
        "name": "Side Effect",
        "registered_date": "2026-01-01"
      },
      {
        "code": "18",
        "name": "Yxl Libre",
        "registered_date": "2026-01-01"
      },
      {
        "code": "19",
        "name": "Yxl Y For Men",
        "registered_date": "2026-01-01"
      },
      {
        "code": "20",
        "name": "9pm Rebel",
        "registered_date": "2026-01-01"
      },
      {
        "code": "21",
        "name": "Powder Room",
        "registered_date": "2026-01-01"
      },
      {
        "code": "22",
        "name": "Mad About You",
        "registered_date": "2026-01-01"
      },
      {
        "code": "23",
        "name": "Signature Elixer Le Male",
        "registered_date": "2026-01-01"
      },
      {
        "code": "24",
        "name": "Ebra Pura",
        "registered_date": "2026-01-01"
      },
      {
        "code": "25",
        "name": "Got My Bojo Back",
        "registered_date": "2026-01-01"
      },
      {
        "code": "26",
        "name": "One Jesmil",
        "registered_date": "2026-01-01"
      },
      {
        "code": "27",
        "name": "Signature Le Male Le Parfum",
        "registered_date": "2026-01-01"
      },
      {
        "code": "28",
        "name": "Maroccan Sunset",
        "registered_date": "2026-01-01"
      },
      {
        "code": "29",
        "name": "Aphrpdite",
        "registered_date": "2026-01-01"
      },
      {
        "code": "30",
        "name": "Dark Secret",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_bhirama_essentials_81",
    "brand_name": "BHIRAMA ESSENTIALS",
    "brand_code": "BHIRAM",
    "variants": [
      {
        "code": "01",
        "name": "White Oud",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Abqari",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Bukhara",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Madawi",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Arabian Oud",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Keukenhof",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Twilight Tropics",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Rainforest Rhapsody",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Black Oud",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Tokyo Rush",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "Marrakech",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Prince Of Doha",
        "registered_date": "2026-01-01"
      },
      {
        "code": "13",
        "name": "Blissfull Atlantique",
        "registered_date": "2026-01-01"
      },
      {
        "code": "14",
        "name": "Oud Wood",
        "registered_date": "2026-01-01"
      },
      {
        "code": "15",
        "name": "African Sunrise",
        "registered_date": "2026-01-01"
      },
      {
        "code": "16",
        "name": "Leather Oud",
        "registered_date": "2026-01-01"
      },
      {
        "code": "17",
        "name": "Granada",
        "registered_date": "2026-01-01"
      },
      {
        "code": "18",
        "name": "Andalusia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "19",
        "name": "Gaza",
        "registered_date": "2026-01-01"
      },
      {
        "code": "20",
        "name": "Oud Al Haramain",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_borcelle_82",
    "brand_name": "BORCELLE",
    "brand_code": "BORCEL",
    "variants": [
      {
        "code": "01",
        "name": "Black Code",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "The Man Potion",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Summer Paradise",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Vanilla Ice",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Imagination",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_bouven_83",
    "brand_name": "BOUVEN",
    "brand_code": "BOUVEN",
    "variants": [
      {
        "code": "01",
        "name": "Ayesha",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Cali",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Atos",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Brahim",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Shabrina",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_boys__amp__women_84",
    "brand_name": "BOYS &amp; WOMEN",
    "brand_code": "BOYSAM",
    "variants": [
      {
        "code": "01",
        "name": "Elora",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Cozy",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Wandermust",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Zenith",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Shiren",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_by_rodiana_85",
    "brand_name": "BY RODIANA",
    "brand_code": "BYRODI",
    "variants": [
      {
        "code": "01",
        "name": "Secret Garden Of Oud",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Oud Kalahari",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Oud Almadani",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "EYE OF HORUZ",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "HAPPY NIKA",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_babagaf_86",
    "brand_name": "BABAGAF",
    "brand_code": "BABAGA",
    "variants": [
      {
        "code": "01",
        "name": "Laif",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Qardam",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_before_sujud_87",
    "brand_name": "BEFORE SUJUD",
    "brand_code": "BEFORE",
    "variants": [
      {
        "code": "01",
        "name": "TEDUH",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "SELARAS",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "DAMAI",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_cheona_88",
    "brand_name": "CHEONA",
    "brand_code": "CHEONA",
    "variants": [
      {
        "code": "01",
        "name": "Poetic Bourbon",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Negroni Leather",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Secret Dance in Marrakech",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Creme Carnival",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Battle of Arctic",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_defy_flower_89",
    "brand_name": "DEFY FLOWER",
    "brand_code": "DEFYFL",
    "variants": [
      {
        "code": "01",
        "name": "Venella",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Baby Cooper",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Vins Blue",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_dein_s_90",
    "brand_name": "DEIN'S",
    "brand_code": "DEINS",
    "variants": [
      {
        "code": "01",
        "name": "Privilege",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Edelweis Miss",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_dialoga_91",
    "brand_name": "DIALOGA",
    "brand_code": "DIALOG",
    "variants": [
      {
        "code": "01",
        "name": "Dawn Sun",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Moon Thousand",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_d_blure_92",
    "brand_name": "D'Blure",
    "brand_code": "DBLURE",
    "variants": [
      {
        "code": "01",
        "name": "Eager Man",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "DJVU",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Lunar Fox",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Feel So Good",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Her Emotions",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_elliote_93",
    "brand_name": "ELLIOTE",
    "brand_code": "ELLIOT",
    "variants": [
      {
        "code": "01",
        "name": "Morning Kiss",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Long Way to the Bar",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_elrajascent_94",
    "brand_name": "ELRAJASCENT",
    "brand_code": "ELRAJA",
    "variants": [
      {
        "code": "01",
        "name": "Renera Omnia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Renera Santal",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Renera De Blue",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Renera De Marley",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Renera Elixir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Renera Elysium",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "Renera Daissy",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Renera Ombre",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "Renera Sables",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Renera Bacca",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_eol_energy_of_love_95",
    "brand_name": "EOL ENERGY OF LOVE",
    "brand_code": "EOLENE",
    "variants": [
      {
        "code": "01",
        "name": "Dark Phoenix",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_everless_96",
    "brand_name": "EVERLESS",
    "brand_code": "EVERLE",
    "variants": [
      {
        "code": "01",
        "name": "D'Lily",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Camelia",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Day",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Night",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_evesol_97",
    "brand_name": "EVESOL",
    "brand_code": "EVESOL",
    "variants": [
      {
        "code": "01",
        "name": "Arunika",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Jinggala",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master__nolla_98",
    "brand_name": "ÉNOLLA",
    "brand_code": "NOLLA",
    "variants": [
      {
        "code": "01",
        "name": "La Grace",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Phantom X",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Classico",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Phantom Lady",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Orchide",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_earthora_99",
    "brand_name": "EARTHORA",
    "brand_code": "EARTHO",
    "variants": [
      {
        "code": "01",
        "name": "THE TEA EDP",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_ergo_fragrance_100",
    "brand_name": "ERGO FRAGRANCE",
    "brand_code": "ERGOFR",
    "variants": [
      {
        "code": "01",
        "name": "NIGHTVEIL",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "MINDFORGE",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "WARBOUND",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_fonce_101",
    "brand_name": "FONCE",
    "brand_code": "FONCE",
    "variants": [
      {
        "code": "01",
        "name": "Cloud Bath",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Tuberose Absolu",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Tuberose De Noir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Jasmin Wood",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Jardin de Fleur",
        "registered_date": "2026-01-01"
      },
      {
        "code": "06",
        "name": "Morning Tea",
        "registered_date": "2026-01-01"
      },
      {
        "code": "07",
        "name": "La Nuit",
        "registered_date": "2026-01-01"
      },
      {
        "code": "08",
        "name": "Masquerade",
        "registered_date": "2026-01-01"
      },
      {
        "code": "09",
        "name": "FLEUR DE COLA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "10",
        "name": "Delish Library",
        "registered_date": "2026-01-01"
      },
      {
        "code": "11",
        "name": "GRAND GALA",
        "registered_date": "2026-01-01"
      },
      {
        "code": "12",
        "name": "Emeraude",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_fragnstan_102",
    "brand_name": "FRAGNSTAN",
    "brand_code": "FRAGNS",
    "variants": [
      {
        "code": "01",
        "name": "Scent of Grace",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Elusive Velour",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Serenade Allure",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_frimery_103",
    "brand_name": "FRIMERY",
    "brand_code": "FRIMER",
    "variants": [
      {
        "code": "01",
        "name": "S.E.A",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "M.O.U.N.T",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_gamal_104",
    "brand_name": "GAMAL",
    "brand_code": "GAMAL",
    "variants": [
      {
        "code": "01",
        "name": "Wembley",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Lugano",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_gazla_by_umy_fadilla_105",
    "brand_name": "GAZLA BY UMY FADILLA",
    "brand_code": "GAZLAB",
    "variants": [
      {
        "code": "01",
        "name": "Soul",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_ghaier_106",
    "brand_name": "GHAIER",
    "brand_code": "GHAIER",
    "variants": [
      {
        "code": "01",
        "name": "Silver Song Of A Lark",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Melancholy Verse Of A Forgotten Flower",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "3 Am Version Of You",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Electric Love For The Strangers",
        "registered_date": "2026-01-01"
      },
      {
        "code": "05",
        "name": "Dawn Breaks In The Cayman Islands",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_haske_107",
    "brand_name": "HASKE",
    "brand_code": "HASKE",
    "variants": [
      {
        "code": "01",
        "name": "Touchy",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_hrmn_108",
    "brand_name": "HRMN",
    "brand_code": "HRMN",
    "variants": [
      {
        "code": "01",
        "name": "Hts harmoni Trend Scent For Unisex",
        "registered_date": "2026-01-01"
      }
    ]
  },
  {
    "id": "batch_master_huntparfum_109",
    "brand_name": "HUNTPARFUM",
    "brand_code": "HUNTPA",
    "variants": [
      {
        "code": "01",
        "name": "Golden Elixir",
        "registered_date": "2026-01-01"
      },
      {
        "code": "02",
        "name": "Hazel",
        "registered_date": "2026-01-01"
      },
      {
        "code": "03",
        "name": "Lyria",
        "registered_date": "2026-01-01"
      },
      {
        "code": "04",
        "name": "Veilmoor",
        "registered_date": "2026-01-01"
      }
    ]
  }
];

// Helper: Ambil seluruh data Master Batch (Sinkron dengan Cloud Supabase CompanySettings)
export const fetchBatchMasters = async () => {
  try {
    const settingsList = await base44.entities.CompanySettings.list();
    if (settingsList && settingsList.length > 0 && Array.isArray(settingsList[0].batch_masters) && settingsList[0].batch_masters.length > 0) {
      return settingsList[0].batch_masters;
    }
    
    // Auto-seed to Supabase if not yet present
    if (settingsList && settingsList.length > 0) {
      await base44.entities.CompanySettings.update(settingsList[0].id, {
        batch_masters: INITIAL_BATCH_MASTER
      }).catch(() => {});
    }
    return INITIAL_BATCH_MASTER;
  } catch (err) {
    console.error("Gagal membaca BatchMaster:", err);
    return INITIAL_BATCH_MASTER;
  }
};

// Helper: Simpan atau Update Master Brand ke Cloud Supabase
export const saveBatchMasterList = async (updatedList) => {
  try {
    const settingsList = await base44.entities.CompanySettings.list();
    if (settingsList && settingsList.length > 0) {
      await base44.entities.CompanySettings.update(settingsList[0].id, {
        batch_masters: updatedList
      });
    }
    return updatedList;
  } catch (err) {
    console.error("Gagal menyimpan ke CompanySettings:", err);
    throw err;
  }
};

// Helper: Ambil Riwayat Batch Diterbitkan
export const fetchBatchHistories = async () => {
  try {
    const settingsList = await base44.entities.CompanySettings.list();
    if (settingsList && settingsList.length > 0 && Array.isArray(settingsList[0].batch_histories)) {
      return settingsList[0].batch_histories;
    }
    return [];
  } catch (err) {
    console.error("Gagal membaca ProductionBatch:", err);
    return [];
  }
};

// Helper: Catat Log Batch Baru (Mendukung single object atau array of objects)
export const appendBatchHistory = async (newHistoryEntryOrArray) => {
  try {
    const entries = Array.isArray(newHistoryEntryOrArray) ? newHistoryEntryOrArray : [newHistoryEntryOrArray];
    if (entries.length === 0) return [];
    
    const settingsList = await base44.entities.CompanySettings.list();
    if (settingsList && settingsList.length > 0) {
      const current = Array.isArray(settingsList[0].batch_histories) ? settingsList[0].batch_histories : [];
      const updated = [...entries, ...current];
      await base44.entities.CompanySettings.update(settingsList[0].id, {
        batch_histories: updated
      });
      return updated;
    }
  } catch (err) {
    console.error("Gagal mencatat log batch:", err);
  }
};
// Helper: Cari Varian & Brand di Batch Master
export const findBatchVariant = (batchMasters = [], brandName = '', variantName = '') => {
  const pureBrand = String(brandName || '').split('/')[0].split('(')[0].trim();
  if (!batchMasters || batchMasters.length === 0) {
    return { isFound: false, brandMaster: null, variant: null, brandCode: formatBrandCode(pureBrand), suggestedVariantIndex: '01' };
  }
  const cleanBrand = pureBrand.toLowerCase();
  const cleanVar = (variantName || '').trim().toLowerCase();

  // 1. Cari Brand (Cocok persis terlebih dahulu, baru fallback awalan)
  const brandMaster = batchMasters.find(b => {
    const bName = (b.brand_name || '').trim().toLowerCase();
    const bCode = (b.brand_code || '').trim().toLowerCase();
    return bName === cleanBrand || bCode === cleanBrand;
  }) || batchMasters.find(b => {
    const bName = (b.brand_name || '').trim().toLowerCase();
    return cleanBrand.startsWith(bName) || bName.startsWith(cleanBrand);
  });

  if (!brandMaster) {
    return { 
      isFound: false, 
      isBrandFound: false, 
      brandMaster: null, 
      variant: null, 
      brandCode: formatBrandCode(pureBrand), 
      suggestedVariantIndex: '01' 
    };
  }

  // 2. Cari Varian di dalam Brand
  const variants = brandMaster.variants || [];
  const cleanKey = (str = '') => String(str || '').toLowerCase().replace(/[^a-z0-9]/gi, '').trim();
  const targetCleanKey = cleanKey(variantName);

  const matchedVariant = variants.find(v => {
    const vName = (v.name || '').trim().toLowerCase();
    const vCode = (v.code || '').trim().toLowerCase();
    const vClean = cleanKey(v.name);
    return vName === cleanVar || (targetCleanKey && vClean === targetCleanKey) || cleanVar.includes(vName) || vName.includes(cleanVar) || vCode === cleanVar;
  });

  if (matchedVariant) {
    return {
      isFound: true,
      isBrandFound: true,
      brandMaster,
      variant: matchedVariant,
      brandCode: brandMaster.brand_code || formatBrandCode(brandMaster.brand_name),
      variantIndex: matchedVariant.code || '01',
    };
  }

  return {
    isFound: false,
    isBrandFound: true,
    brandMaster,
    variant: null,
    brandCode: brandMaster.brand_code || formatBrandCode(brandMaster.brand_name),
    suggestedVariantIndex: String(variants.length + 1).padStart(2, '0')
  };
};

// Helper: Daftarkan Varian Baru secara langsung ke Batch Master
export const registerNewVariantToBatchMaster = async (brandName, variantName, customCode) => {
  const masters = await fetchBatchMasters();
  const cleanBrand = (brandName || 'SATALA').trim();
  const cleanVar = (variantName || 'Varian Baru').trim();

  let brandIndex = masters.findIndex(b => (b.brand_name || '').trim().toLowerCase() === cleanBrand.toLowerCase());
  let targetBrand;

  if (brandIndex >= 0) {
    targetBrand = { ...masters[brandIndex] };
  } else {
    targetBrand = {
      id: `batch_master_${Date.now()}`,
      brand_name: cleanBrand,
      brand_code: generateUniqueBrandCode(cleanBrand, '', masters),
      variants: []
    };
    masters.push(targetBrand);
    brandIndex = masters.length - 1;
  }

  const existingVariants = targetBrand.variants || [];
  const nextCode = customCode || String(existingVariants.length + 1).padStart(2, '0');
  
  const newVariant = {
    code: nextCode,
    name: cleanVar,
    registered_date: new Date().toISOString().slice(0, 10)
  };

  targetBrand.variants = [...existingVariants, newVariant];
  masters[brandIndex] = targetBrand;

  await saveBatchMasterList(masters);
  return { brandMaster: targetBrand, variant: newVariant };
};
